
import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { Client, CreateMeterDataIn, GsmProviders, GuidDictionaryItem, GuidParentMeterDataOutDictionaryItem, MeterAutentification, MeterParentGetDataIn, MeterPhase, MeterType, ParentMeterDataOut, PermissionsEnum, RatioDataIn } from '../../../api/api-reference';
import { TimeZoneService } from '../../../services/timezone.service';
import { Observable, Subscription } from 'rxjs';
import { DateRangeInput } from '../../core/models/date-range-input';
import { DatepickerComponent } from '../../core/components/datepicker/datepicker.component';
import { formatDateDDMMMYYYY, toUtcDate } from '../../../helper/date-utils';
import { DropdownSearchComponent } from '../components/parent-meter-dropdown-search/parent-meter-dropdown-search.component';

@Component({
  selector: 'app-real-device-setup',
  standalone: true,
  imports: [ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    CommonModule,
    AngularSvgIconModule,
    RouterLink,
    DatepickerComponent,
    DropdownSearchComponent
  ],
  providers: [Client, DatePipe],
  templateUrl: './device-setup.component.html',
  styleUrl: './device-setup.component.scss'
})
export class DeviceSetupComponent implements OnInit {
  submitting: boolean = false;
  submitted: boolean = false;
  cellularSimSerialNumberError: string = "";
  selectedDate: string | null = '';
  selectedParent: ParentMeterDataOut | null = null;
  selectedAssetType: string = '';

  deviceForm: FormGroup = this.fb.group({
    basicInfo: this.fb.group({
      serviceName: ['', Validators.required],
      serviceNumber: [null],
      assetType: ['', Validators.required],
      address: ['', Validators.required],
      region: ['', Validators.required],
      area: [{ value: '', disabled: true }, Validators.required],
      serviceType: [null],
      parent: [null],
    }),
    meterInfo: this.fb.group({
      meterNumber: ['', [Validators.required, Validators.pattern('^[0-9]+$')]],
      meterMake: ['', Validators.required],
      meterModel: [{ value: '', disabled: true }, Validators.required],
      meterPhase: [{ value: '', disabled: true }],
      meterType: [null, Validators.required],
      currentTransformerRatioNumerator: [null, Validators.min(1)],
      currentTransformerRatioDenominator: [null, Validators.min(1)],
      voltageTransformerRatioNumerator: [null, Validators.min(1)],
      voltageTransformerRatioDenominator: [null, Validators.min(1)],
      meterTimeZone: [null, Validators.required],
      meterManufactureDate: [null],
    }),
    connectivity: this.fb.group({
      gsmProvider: ['', Validators.required],
      cellularSimSerialNumber: ['', Validators.required],
      meterIp: ['', [Validators.required, Validators.pattern('^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$')]],
      meterPort: [null, [Validators.required, Validators.min(1024), Validators.max(49151)]],
      meterInterface: [{ value: '', disabled: true }],
      dataCollectionInterval: [null, Validators.required],
      clientAddress: [{ value: '', disabled: true }],
    }),
    installationTiming: this.fb.group({
      serviceLatitude: [null, [Validators.min(-90), Validators.max(90)]],
      serviceLongitude: [null, [Validators.min(-180), Validators.max(180)]],
      meterInstallationDate: [null, Validators.required],
      clockOffset: [null, Validators.min(30)],
    }),
    authentication: this.fb.group({
      autentificationType: [{ value: '', disabled: true }],
      blockCipherKey: [null, Validators.maxLength(32)],
      authenticationKey: [null, Validators.maxLength(32)],
      meterSecurity: [{ value: '', disabled: true }],
      password: [{ value: null, disabled: true }],
      newPassword: [{ value: null, disabled: true }, [Validators.pattern('^\\S{8}$')]],
      repeatNewPassword: [{ value: null, disabled: true }],
    })
  });
  intervals = [
    { value: 15 },
    { value: 30 },
    { value: 60 },
    { value: 1440 },
  ];
  meterTypeDictionary: MeterType[] = Object.keys(MeterType)
    .filter((key) => isNaN(Number(key)))
    .map((key) => MeterType[key as keyof typeof MeterType]);

  gsmProviderDictionary: GsmProviders[] = Object.keys(GsmProviders)
    .filter((key) => isNaN(Number(key)))
    .map((key) => GsmProviders[key as keyof typeof GsmProviders]);

  regions: GuidDictionaryItem[] = [];
  serviceTypes: GuidDictionaryItem[] = [];
  meterModels: GuidDictionaryItem[] = [];
  meterMakers: GuidDictionaryItem[] = [];
  areas: GuidDictionaryItem[] = [];
  assetTypes: GuidDictionaryItem[] = [];
  MeterAutentification = MeterAutentification;
  timezones = this.timezoneService.timezones;
  public PermissionsEnum = PermissionsEnum;
  isDatepickerShown = false;
  isManufactureDatepickerShown = false;
  selectedManufactureDate: string | null = '';
  showPassword: boolean = false;
  changePasswordOnMeter = false;
  private subscriptions: Subscription[] = [];
  constructor(private fb: FormBuilder,
    private toastr: ToastrService,
    private router: Router,
    private datePipe: DatePipe,
    private client: Client,
    private timezoneService: TimeZoneService
  ) { }
  ngOnInit(): void {
    const stateData = history.state?.data;
    if (stateData) {
      this.getMeterModelsOfMaker(stateData.meterMake);
      this.deviceForm.patchValue(stateData);
      this.getMeterModel(stateData.meterModel);
    }
    this.getAssetTypes();
    this.getRegions();
    this.getServiceTypes();
    this.getMeterMakers();

    const allProviders = Object.values(GsmProviders);
    if (allProviders.length === 1) {
      this.deviceForm.get('connectivity.gsmProvider')?.setValue(allProviders[0]);
    }

    const subscription = this.deviceForm.valueChanges.subscribe(() => {
      const x = this.deviceForm.getRawValue();

      this.onAuthTypeChange();
      this.onNewPasswordChange();
      this.enableRequiredValidatorsForKeys(['currentTransformerRatioNumerator', 'currentTransformerRatioDenominator'], x.currentTransformerRatioNumerator != null || x.currentTransformerRatioDenominator != null);
      this.enableRequiredValidatorsForKeys(['voltageTransformerRatioNumerator', 'voltageTransformerRatioDenominator'], x.voltageTransformerRatioNumerator != null || x.voltageTransformerRatioDenominator != null);
    });

    this.subscriptions.push(subscription);
  }
  resetDeviceForm() {
    this.deviceForm.reset();
    this.submitted = false;
  }
  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
  getMeterModelsOfMaker(meterMake: string) {
    if (meterMake != null) {
      this.client.getAllMeterModelForOptions(meterMake).subscribe({
        next: (response) => {
          this.meterModels = response ?? [];
        },
        error: (err) => {
          this.toastr.error(err);
        },
      });
    } else {
      this.meterModels = [];
    }
  }

  enableRequiredValidatorsForKeys(keys: string[], shouldEnable: boolean) {
    keys.forEach((key) => {
      const control = this.deviceForm.get(key);
      if (control) {
        if (!shouldEnable) {
          control.removeValidators(Validators.required)
        } else {
          control.addValidators(Validators.required);
        }
        control.updateValueAndValidity({ emitEvent: false });
      }
    });
  }

  onAuthTypeChange() {
    const isLow = this.deviceForm.get('authentication.autentificationType')?.value === this.MeterAutentification.Low;
    const passwordCtrl = this.deviceForm.get('authentication.password');
    if (isLow) {
      passwordCtrl?.enable({ emitEvent: false });
      passwordCtrl?.addValidators(Validators.required);
      passwordCtrl?.updateValueAndValidity({ emitEvent: false });
    } else {
      if (this.changePasswordOnMeter) {
        this.changePasswordOnMeter = false;
        ['authentication.newPassword', 'authentication.repeatNewPassword'].forEach(name => {
          const ctrl = this.deviceForm.get(name);
          ctrl?.removeValidators(Validators.required);
          ctrl?.setValue(null, { emitEvent: false });
          ctrl?.disable({ emitEvent: false });
        });
      }
      this.deviceForm.patchValue({ authentication: { password: undefined } }, { emitEvent: false });
      passwordCtrl?.removeValidators(Validators.required);
      passwordCtrl?.disable({ emitEvent: false });
    }
  }

  onChangePasswordCheckboxToggle(): void {
    if (this.changePasswordOnMeter) {
      ['authentication.newPassword', 'authentication.repeatNewPassword'].forEach(name => {
        const ctrl = this.deviceForm.get(name);
        ctrl?.addValidators(Validators.required);
        ctrl?.enable({ emitEvent: false });
        ctrl?.updateValueAndValidity({ emitEvent: false });
      });
    } else {
      ['authentication.newPassword', 'authentication.repeatNewPassword'].forEach(name => {
        const ctrl = this.deviceForm.get(name);
        ctrl?.removeValidators(Validators.required);
        ctrl?.setValue(null, { emitEvent: false });
        ctrl?.disable({ emitEvent: false });
      });
    }
  }

  onNewPasswordChange() {
    const repeat = this.deviceForm.get('authentication.repeatNewPassword');
    if (this.deviceForm.get('authentication.newPassword')?.value) {
      if (repeat?.disabled) {
        repeat.enable({ emitEvent: false });
      }
    } else if (repeat?.enabled) {
      repeat.setValue(null, { emitEvent: false });
      repeat.disable({ emitEvent: false });
    }
  }

  get newPasswordEntered(): boolean {
    return !!this.deviceForm.get('authentication.newPassword')?.value;
  }

  get repeatPasswordState(): 'prompt' | 'match' | 'mismatch' {
    const newPassword = this.deviceForm.get('authentication.newPassword')?.value;
    const repeat = this.deviceForm.get('authentication.repeatNewPassword')?.value;
    if (!repeat) return 'prompt';
    return newPassword === repeat ? 'match' : 'mismatch';
  }

  get passwordsMismatch(): boolean {
    return this.newPasswordEntered && this.repeatPasswordState !== 'match';
  }

  get isLowAuthType(): boolean {
    return this.deviceForm.get('authentication.autentificationType')?.value === this.MeterAutentification.Low;
  }
  getRegions() {
    this.client.getAllRegionsForOptions().subscribe({
      next: (response) => {
        this.regions = response ?? [];
        const areaControl = this.deviceForm.get('basicInfo.area');
        areaControl?.setValue('');
        areaControl?.markAsPristine();
        areaControl?.markAsUntouched();
        areaControl?.disable();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  getIpFromGsmProvider() {
    const gsmProvider = this.deviceForm.get('connectivity.gsmProvider')?.value;
    const simSerial = this.deviceForm.get('connectivity.cellularSimSerialNumber')?.value;
    if (simSerial) {
      this.client.getIpAddressFromGsmProvider(gsmProvider, simSerial).subscribe({
        next: (response) => {
          this.deviceForm.get('connectivity.meterIp')?.setValue(response);
          this.cellularSimSerialNumberError = "";
        },
        error: (err) => {
          this.deviceForm.get('connectivity.meterIp')?.setValue('');
          this.cellularSimSerialNumberError = err;
        },
      });
    }
  }

  selectRadioOption(fieldName: string, value: any) {
    this.deviceForm.get(`meterInfo.${fieldName}`)?.setValue(value);
  }

  onDateRangeChanged(input: DateRangeInput): void {
    const startDate = new Date(input.dateFrom);
    this.deviceForm.patchValue({
      installationTiming: {
        meterInstallationDate: toUtcDate(startDate)
      }
    });
    this.selectedDate = formatDateDDMMMYYYY(startDate)
    this.toggleDatepicker();
  }
  toggleDatepicker() {
    this.isDatepickerShown = !this.isDatepickerShown;
  }

  toggleManufactureDatepicker() {
    this.isManufactureDatepickerShown = !this.isManufactureDatepickerShown;
  }

  clearManufactureDate() {
    this.deviceForm.patchValue({ meterInfo: { meterManufactureDate: null } });
    this.selectedManufactureDate = '';
  }

  onManufactureDateChanged(input: DateRangeInput): void {
    const startDate = new Date(input.dateFrom);
    this.deviceForm.patchValue({
      meterInfo: {
        meterManufactureDate: toUtcDate(startDate)
      }
    });
    this.selectedManufactureDate = formatDateDDMMMYYYY(startDate);
    this.toggleManufactureDatepicker();
  }

  preventInvalidInput(event: KeyboardEvent): void {
    const invalidChars = ['e', 'E', '+', '-', '.'];
    if (invalidChars.includes(event.key)) {
      event.preventDefault();
    }
  }

  getMeterMakers() {
    this.client.getAllMeterMakersForSetupOptions().subscribe({
      next: (response) => {
        this.meterMakers = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getAssetTypes() {
    this.client.getAllAssetTypesForOptions(undefined).subscribe({
      next: (response) => {
        this.assetTypes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err.message);
      },
    });
  }

  getServiceTypes() {
    this.client.getAllServiceTypesForOptions().subscribe({
      next: (response) => {
        this.serviceTypes = response ?? [];
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  formatInterval(minutes: number): string {
    if (minutes >= 60) {
      const hours = minutes / 60;
      return `${hours}h`;
    } else {
      return `${minutes}mins`;
    }
  }
  getMeterModel(meterModel: string) {
    this.client.getMeterModel(meterModel).subscribe({
      next: (response) => {
        this.deviceForm.patchValue({
          meterInfo: {
            meterPhase: response.meterPhase
          },
          connectivity: {
            meterInterface: response.interface,
            clientAddress: response.clientAddress
          },
          authentication: {
            autentificationType: response.authorizationLevel,
            meterSecurity: response.security
          }
        });
        this.onAuthTypeChange();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
  getMeterModels() {
    const meterMake = this.deviceForm.get('meterInfo.meterMake')?.value;
    this.client.getAllMeterModelForOptions(meterMake).subscribe({
      next: (response) => {
        this.meterModels = response ?? [];

        const meterModelControl = this.deviceForm.get('meterInfo.meterModel');
        meterModelControl?.setValue('');
        meterModelControl?.markAsPristine();
        meterModelControl?.markAsUntouched();
        this.meterModels.length > 0 ? meterModelControl?.enable() : meterModelControl?.disable();

        this.deviceForm.patchValue({
          meterInfo: {
            meterPhase: '',
          },
          connectivity: {
            meterInterface: '',
            clientAddress: ''
          },
          authentication: {
            autentificationType: '',
            meterSecurity: '',
          }
        });
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  getAreasByRegion(event: Event) {
    const selectedElement = event.target as HTMLSelectElement;
    const selectedValue = selectedElement.value;
    this.client.getAllAreaByRegionForOptions(selectedValue).subscribe({
      next: (response) => {
        this.areas = response ?? [];
        const areaControl = this.deviceForm.get('basicInfo.area');
        areaControl?.setValue('');
        areaControl?.markAsPristine();
        areaControl?.markAsUntouched();
        this.areas.length > 0 ? areaControl?.enable() : areaControl?.disable();
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }


  formErrorsExists(controlName: string): boolean {
    const control = this.deviceForm.get(controlName);
    return control ? control.invalid && (control.dirty || control.touched) : false;
  }

  onAssetTypeSelected(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.selectedAssetType = value;
    const parentControl = this.deviceForm.get('basicInfo.parent');
    parentControl?.setValue(null);
    parentControl?.markAsPristine();
    parentControl?.markAsUntouched();
    this.selectedParent = null;
  }

  onParentSelected(value: ParentMeterDataOut | null): void {
    if (value == null) {
      this.selectedParent = value;
      this.deviceForm.get('basicInfo.parent')?.setValue(null);
    }
    else {
      this.selectedParent = value;
      this.deviceForm.get('basicInfo.parent')?.setValue(value.id);
    }
  }

  fetchParents = (search: string): Observable<GuidParentMeterDataOutDictionaryItem[]> => {
    const request = new MeterParentGetDataIn();
    request.assetTypeId = this.selectedAssetType;
    request.search = search;
    return this.client.getParentsForOption(request);
  }


  onSubmit() {
    if (this.deviceForm.invalid) {
      this.deviceForm.markAllAsTouched();
      return;
    }

    if (this.changePasswordOnMeter && this.passwordsMismatch) {
      return;
    }

    const formValue = this.deviceForm.getRawValue();
    const { repeatNewPassword: _rnp, ...authRest } = formValue.authentication;
    const data: CreateMeterDataIn = {
      ...formValue.basicInfo,
      ...formValue.meterInfo,
      ...formValue.connectivity,
      ...formValue.installationTiming,
      ...authRest
    };

    if (formValue.meterInfo.currentTransformerRatioNumerator && formValue.meterInfo.currentTransformerRatioDenominator) {
      data.currentTransformerRatio = new RatioDataIn({
        numerator: formValue.meterInfo.currentTransformerRatioNumerator,
        denominator: formValue.meterInfo.currentTransformerRatioDenominator
      });
    }

    if (formValue.meterInfo.voltageTransformerRatioNumerator && formValue.meterInfo.voltageTransformerRatioDenominator) {
      data.voltageTransformerRatio = new RatioDataIn({
        numerator: formValue.meterInfo.voltageTransformerRatioNumerator,
        denominator: formValue.meterInfo.voltageTransformerRatioDenominator
      });
    }
    this.client.addDevice(data).subscribe({
      next: () => {
        this.toastr.success('Successfully saved.');
        this.submitted = true;
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }
}
export enum DeviceSetupTabs {
  BasicInformation = "DEVICESETUPTABS_BASICINFO",
  MeterInformation = "DEVICESETUPTABS_METERINFO",
  Connectivity = "DEVICESETUPTABS_CONNECTIVITY",
  InstallationTiming = "DEVICESETUPTABS_INSTALLATIONTIMING",
  Authentication = "DEVICESETUPTABS_AUTHENTICATION",
  Complete = "DEVICESETUPTABS_COMPLETE"
}
